/**
 * job_mileage — miles and fuel cost per delivered load (DEC-326 item 3a, migration 0145).
 *
 * Filled and recomputed by the mileage sweeper (packages/api services/mileage), because
 * a load's GPS trail usually syncs after the load completes. See the migration header for
 * the rule (GPS trail / straight line x 1.3 / none) and why counts, not timestamps, mark
 * what each row has seen. This mirror keeps schema-diff tooling aligned; the API reads and
 * writes it with raw SQL.
 */

import { sql } from 'drizzle-orm';
import {
  check,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { jobs } from './jobs.js';
import { tenants } from './users.js';

export const jobMileage = pgTable(
  'job_mileage',
  {
    jobId: uuid('job_id')
      .primaryKey()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** 'GPS_TRAIL' | 'STRAIGHT_LINE' | 'NONE'. */
    method: text('method').notNull(),
    miles: numeric('miles', { precision: 9, scale: 2 }),
    /** STRAIGHT_LINE only: crow-flies miles before the road factor. */
    straightLineMiles: numeric('straight_line_miles', { precision: 9, scale: 2 }),
    /** STRAIGHT_LINE only: 1.30 (Corbin, 2026-10-13). */
    roadFactor: numeric('road_factor', { precision: 4, scale: 2 }),
    pointCount: integer('point_count').notNull().default(0),
    maxGapMinutes: integer('max_gap_minutes'),
    rateCentsPerMileUsed: integer('rate_cents_per_mile_used'),
    fuelCostCents: integer('fuel_cost_cents'),
    trailPointsSeen: integer('trail_points_seen').notNull().default(0),
    eventsSeen: integer('events_seen').notNull().default(0),
    computedAt: timestamp('computed_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('job_mileage_tenant_idx').on(t.tenantId),
    check('job_mileage_method_check', sql`method IN ('GPS_TRAIL', 'STRAIGHT_LINE', 'NONE')`),
    check('job_mileage_miles_check', sql`(method = 'NONE') = (miles IS NULL)`),
    check(
      'job_mileage_straight_line_check',
      sql`(method = 'STRAIGHT_LINE') = (road_factor IS NOT NULL AND straight_line_miles IS NOT NULL)`,
    ),
    check(
      'job_mileage_fuel_check',
      sql`fuel_cost_cents IS NULL OR (miles IS NOT NULL AND rate_cents_per_mile_used IS NOT NULL AND fuel_cost_cents >= 0)`,
    ),
    pgPolicy('job_mileage_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type JobMileage = typeof jobMileage.$inferSelect;
